import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, UsersRound, ShieldCheck, ClipboardCheck, AlertTriangle, ArrowRight, GraduationCap, BookOpen, Award } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const ACTIVE_KEYS = new Set(["organization_created", "admin_assigned", "departments_created"]);

const ONBOARDING_LABELS = [
  ["organization_created", "Organization Created"],
  ["admin_assigned", "Admin Assigned"],
  ["departments_created", "Departments Created"],
  ["staff_enrollment_pending", "Staff Enrollment (Phase 4)"],
  ["course_mapping_pending", "Course Mapping (Phase 5)"],
  ["training_pending", "Training (Phase 6)"],
];

export default function HospitalAdminDashboard() {
  const [dash, setDash] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [subInfo, setSubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspended, setSuspended] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, o, s] = await Promise.all([
        api.get("/org/dashboard-summary"),
        api.get("/org/onboarding"),
        api.get("/org/billing/dashboard").catch(() => ({ data: { data: null } })),
      ]);
      setDash(d.data.data);
      setOnboarding(o.data.data.onboarding);
      setSubInfo(s.data.data || null);
    } catch (err) {
      if (err?.response?.status === 403) setSuspended(true);
      else toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <DashboardLayout title="Institute Overview"><LoadingBlock label="Loading your organization…" /></DashboardLayout>;
  }

  if (suspended) {
    return (
      <DashboardLayout title="Access restricted">
        <div className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-white p-10 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h2 className="mt-6 text-2xl font-black text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Organization suspended</h2>
          <p className="mt-2 text-sm text-slate-600">
            Your organization account is currently suspended. Please contact Steward Support to restore access.
          </p>
          <a href="mailto:info@stewardindia.com" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#101876] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0c135c]">
            Contact Steward Support
          </a>
        </div>
      </DashboardLayout>
    );
  }

  if (!dash) return null;
  const org = dash.organization;
  const t = dash.totals;

  return (
    <DashboardLayout title={`${org.name}`} breadcrumbs={[org.name, "Dashboard"]}>
      <div className="space-y-8" data-testid="dashboard-hospital-admin">
        {/* Org banner */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="p-6 relative" style={{ background: `linear-gradient(135deg, ${org.primary_color}12, ${org.secondary_color}12)` }}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color: org.secondary_color }}>{(org.type || "").replace(/_/g, " ")}</div>
                <h2 className="mt-1 text-3xl font-black" style={{ color: org.primary_color, fontFamily: "Cabinet Grotesk, sans-serif" }}>{org.name}</h2>
                <div className="mt-2 flex items-center gap-3">
                  <StatusBadge status={org.status} />
                  <span className="text-xs font-semibold text-slate-500">{org.code}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Link to="/app/hospital/profile" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]">
                  Organization profile <ArrowRight className="h-3 w-3" />
                </Link>
                <Link to="/app/hospital/onboarding" className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0c135c]">
                  Onboarding
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Staff" value={t.users} icon="UsersRound" tone="navy" testId="kpi-staff" index={0} to="/app/hospital/staff" />
          <StatCard label="Departments" value={t.departments} icon="Building" tone="green" testId="kpi-departments" note={`${t.active_departments} active`} index={1} to="/app/hospital/departments" />
          <StatCard label="Faculty" value={t.faculty} icon="BookOpen" tone="slate" testId="kpi-faculty" index={2} to="/app/hospital/staff?role=faculty" />
          <StatCard label="Learners" value={t.students} icon="GraduationCap" tone="green" testId="kpi-students" index={3} to="/app/hospital/staff?role=student" />
        </section>

        {subInfo?.plan && (() => {
          const eff = subInfo.effective_status || "active";
          const days = subInfo.days_remaining;
          const seat = subInfo.seat_usage || {};
          const border = eff === "expired" ? "border-rose-300 bg-rose-50" : eff === "in_grace" ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-white";
          const pillCls = eff === "expired" ? "bg-rose-100 text-rose-800" : eff === "in_grace" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";
          return (
            <section className={`rounded-2xl border-2 ${border} p-5`} data-testid="hospital-current-plan-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#101876]/5 text-[#101876]"><Building2 className="h-5 w-5" /></div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Current plan</div>
                    <div className="mt-0.5 flex items-center gap-3 flex-wrap">
                      <div className="text-xl font-black text-[#101876]">{subInfo.plan.plan_name}</div>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${pillCls}`}>
                        {eff === "in_grace" ? "IN GRACE" : eff}{(eff === "active" || eff === "in_grace") && ` · ${days}d left`}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {seat.seats_used || 0} / {seat.seats_purchased || 0} seats used · Valid till {(subInfo.subscription?.end_date || "").slice(0, 10)}
                    </div>
                  </div>
                </div>
                <Link to="/app/hospital/billing" className="inline-flex items-center gap-1.5 rounded-md border border-[#101876] bg-white px-3 py-1.5 text-xs font-bold text-[#101876] hover:bg-[#101876]/5" data-testid="hospital-billing-link">
                  Manage billing <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </section>
          );
        })()}

        {/* Quick actions — the most common institute-admin workflows */}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="hospital-quick-actions">
          <QuickAction to="/app/hospital/learning-progress" icon={GraduationCap} title="Staff learning progress" hint="See % completion per learner & course" testId="quick-learning-progress" />
          <QuickAction to="/app/hospital/reports/course-completion" icon={ClipboardCheck} title="Course completion report" hint="Filter by course, department, status" testId="quick-course-completion" />
          <QuickAction to="/app/hospital/reports/training-matrix" icon={UsersRound} title="Training matrix" hint="Staff × mandatory-course grid" testId="quick-training-matrix" />
          <QuickAction to="/app/hospital/course-assignments" icon={BookOpen} title="Course assignments" hint="Assign & manage learner enrollments" testId="quick-course-assignments" />
        </section>

        <section className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Setup progress</div>
            <h3 className="text-lg font-bold text-slate-900">Onboarding checklist</h3>
            <div className="mt-3 flex items-center gap-3">
              <Progress value={onboarding?.completion_percentage || 0} className="h-2 flex-1" />
              <span className="text-sm font-bold text-slate-900">{onboarding?.completion_percentage || 0}%</span>
            </div>
            <ul className="mt-6 space-y-2">
              {ONBOARDING_LABELS.map(([key, label]) => {
                const done = onboarding?.[key];
                return (
                  <li key={key} className="flex items-center gap-3 text-sm">
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${done ? "bg-[#329112] text-white" : "bg-slate-100 text-slate-400"}`}>
                      <ClipboardCheck className="h-3 w-3" />
                    </span>
                    <span className={done ? "text-slate-900" : "text-slate-500"}>{label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="grid gap-4">
            <Link to="/app/hospital/courses" data-testid="hospital-assigned-courses-link" className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#101876]/40 hover:shadow-md transition">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#101876]/5 text-[#101876]">
                  <BookOpen className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-base font-bold text-slate-900">Assigned courses</h3>
                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#101876] group-hover:translate-x-0.5 transition" />
                  </div>
                  <p className="mt-1 text-sm text-slate-500 leading-relaxed">Browse the course catalog mapped to your institute.</p>
                </div>
              </div>
            </Link>
            <Link to="/app/hospital/certificates/dashboard" data-testid="hospital-certificates-link" className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#329112]/40 hover:shadow-md transition">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#329112]/10 text-[#329112]">
                  <Award className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-base font-bold text-slate-900">Certificates</h3>
                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#329112] group-hover:translate-x-0.5 transition" />
                  </div>
                  <p className="mt-1 text-sm text-slate-500 leading-relaxed">Issue and manage QR-verified certificates.</p>
                </div>
              </div>
            </Link>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function QuickAction({ to, icon: Icon, title, hint, testId }) {
  return (
    <Link to={to} data-testid={testId} className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#101876]/40 hover:shadow-md transition flex items-start gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#101876]/5 text-[#101876]">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-bold text-slate-900">{title}</h4>
          <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#101876] group-hover:translate-x-0.5 transition" />
        </div>
        <p className="mt-1 text-xs text-slate-500 leading-relaxed">{hint}</p>
      </div>
    </Link>
  );
}
